import { Injectable, signal } from '@angular/core'

/**
 * The one provider behind every key-requiring basemap. Both MapTiler satellite
 * configs share a single key, so the dialog names the provider, not the config.
 */
export const MAPTILER_KEY_PROVIDER = {
  name: 'MapTiler',
  keysUrl: 'https://cloud.maptiler.com/account/keys/',
} as const

/**
 * BasemapApiKeyService — manages the single shared MapTiler API key used by the
 * key-requiring satellite basemaps. The key is persisted in localStorage (per
 * browser) and exposed as a signal so every map control reacts when it changes,
 * here or in another tab. Collecting a missing key is the job of
 * BasemapApiKeyDialogService; this service never prompts.
 */
@Injectable({ providedIn: 'root' })
export class BasemapApiKeyService {
  static readonly STORAGE_KEY = 'rse.basemap.apikey.maptiler'

  /** The stored key, or null. Kept in step with localStorage, including other tabs. */
  readonly key = signal<string | null>(this.getKey())

  constructor() {
    // Root singleton: the listener lives as long as the page.
    window.addEventListener('storage', (e: StorageEvent) => {
      // key === null: another tab called localStorage.clear()
      if (e.key === null || e.key === BasemapApiKeyService.STORAGE_KEY) this.key.set(this.getKey())
    })
  }

  getKey(): string | null {
    try {
      return localStorage.getItem(BasemapApiKeyService.STORAGE_KEY)
    } catch {
      return null
    }
  }

  /** Returns false when the browser refused to persist the key. */
  storeKey(key: string): boolean {
    try {
      localStorage.setItem(BasemapApiKeyService.STORAGE_KEY, key)
    } catch {
      return false
    }
    this.key.set(key)
    return true
  }

  clearKey(): void {
    try {
      localStorage.removeItem(BasemapApiKeyService.STORAGE_KEY)
    } catch {
      /* nothing to clear if storage is unavailable */
    }
    this.key.set(null)
  }
}
